// Added parts for one service: First Communion, a blessing, a commissioning.
// Each part names the place in the service it follows; the bulletin scripts
// pour its words right after that place (Jack, 2026-10-23). A part's words
// are written once and kept in the parts library; this service only records
// which parts it uses and where, as "id@place|id@place" in its choices.
import React, { useEffect, useState } from 'react';
import apiClient from '../../utils/apiClient';

export interface AddedPart {
  id: number; church_id: number; name: string; after: string;
  words_en: string; words_es: string; in_library: boolean; revision: number;
}
interface Place { key: string; label: string }
export interface PartUse { id: number; after: string }

export function unpackParts(packed: string): PartUse[] {
  const out: PartUse[] = [];
  for (const entry of (packed || '').split('|')) {
    const m = entry.trim().match(/^(\d+)@([a-z-]+)$/);
    if (m && !out.some(p => p.id === Number(m[1]))) out.push({ id: Number(m[1]), after: m[2] });
  }
  return out;
}
export const packParts = (parts: PartUse[]) => parts.map(p => `${p.id}@${p.after}`).join('|');

type LineType = 'heading' | 'celebrant' | 'people' | 'rubric';
/** The same reading of the marks as the export (backend services/addedParts.ts). */
export function parseWords(words: string): { type: LineType; text: string; gap: boolean }[] {
  const lines: { type: LineType; text: string; gap: boolean }[] = [];
  let gap = false;
  for (const raw of (words || '').replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) { if (lines.length) gap = true; continue; }
    const mark = line.charAt(0);
    const text = /^[#~*]/.test(line) ? line.slice(1).trim() : line;
    if (!text) continue;
    lines.push({ type: mark === '#' ? 'heading' : mark === '~' ? 'rubric' : mark === '*' ? 'people' : 'celebrant', text, gap });
    gap = false;
  }
  return lines;
}

const Preview: React.FC<{ words: string }> = ({ words }) => {
  const lines = parseWords(words);
  if (!lines.length) return <p className="added-part-preview empty">Nothing yet.</p>;
  return <div className="added-part-preview">
    {lines.map((l, i) => <p key={i} className={`line-${l.type}${l.gap ? ' gap' : ''}`}>{l.text}</p>)}
  </div>;
};

interface Editor {
  id?: number; revision?: number; name: string; after: string;
  words_en: string; words_es: string; in_library: boolean;
}
interface Props {
  churchId: number;
  /** This service's parts, packed. */
  value: string;
  onChange: (packed: string) => void;
  /** The 12:30 prints the Spanish words; the 8 am and 10 am the English. */
  language: 'en' | 'es';
}
const messageOf = (error: any) => {
  const body = error.response?.data?.error;
  return typeof body === 'string' ? body : 'Could not load or save the part. Please try again.';
};

export const AddedPartsPanel: React.FC<Props> = ({ churchId, value, onChange, language }) => {
  const uses = unpackParts(value);
  const [places, setPlaces] = useState<Place[]>([]);
  const [parts, setParts] = useState<AddedPart[]>([]);
  const [pick, setPick] = useState('');
  const [editor, setEditor] = useState<Editor | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ids = uses.map(u => u.id).join(',');

  useEffect(() => {
    let cancelled = false;
    apiClient.get<{ places: Place[]; parts: AddedPart[] }>('/bulletins/indesign-settings/added-parts', { params: { churchId, ids } })
      .then(res => { if (!cancelled) { setPlaces(res.data.places); setParts(res.data.parts); } })
      .catch(err => { if (!cancelled) setError(messageOf(err)); });
    return () => { cancelled = true; };
  }, [churchId, ids]);

  const partOf = (id: number) => parts.find(p => p.id === id);
  const placeLabel = (key: string) => places.find(p => p.key === key)?.label || key;
  const library = parts.filter(p => p.in_library && !uses.some(u => u.id === p.id));
  const change = (next: PartUse[]) => onChange(packParts(next));

  const save = async () => {
    if (!editor) return;
    setBusy(true); setError('');
    try {
      const body = { church_id: churchId, name: editor.name.trim(), after: editor.after,
        words_en: editor.words_en, words_es: editor.words_es, in_library: editor.in_library,
        ...(editor.id ? { revision: editor.revision } : {}) };
      const res = editor.id
        ? await apiClient.put<AddedPart>(`/bulletins/indesign-settings/added-parts/${editor.id}`, body)
        : await apiClient.post<AddedPart>('/bulletins/indesign-settings/added-parts', body);
      const saved = res.data;
      setParts(prev => [...prev.filter(p => p.id !== saved.id), saved].sort((a, b) => a.name.localeCompare(b.name)));
      // A new part goes into this service at its place; an edited one keeps the
      // place this service already gives it.
      if (!editor.id) change([...uses, { id: saved.id, after: saved.after }]);
      setEditor(null);
    } catch (err) { setError(messageOf(err)); }
    finally { setBusy(false); }
  };

  return <div className="eight-am-template-panel added-parts-panel">
    <h4>Added parts for this service</h4>
    <p>Anything this service has beyond its usual order, such as First Communion. Each part goes right after the place you choose. Use “Save this Sunday only” above to keep the list for this Sunday; otherwise it is used for this export only.</p>
    {error && <p className="error-message" role="alert">{error}</p>}

    {uses.length === 0 && <p>None.</p>}
    {uses.map((use, i) => {
      const part = partOf(use.id);
      const words = part ? (language === 'es' ? part.words_es : part.words_en) : '';
      return <div key={use.id} className="added-part-row">
        <strong>{part?.name || `Part ${use.id}`}</strong>
        <label>After <select aria-label={`Where ${part?.name || 'this part'} goes`} value={use.after}
          onChange={e => change(uses.map((u, j) => j === i ? { ...u, after: e.target.value } : u))}>
          {places.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select></label>
        {part && !words.trim() && <span className="error-message">No {language === 'es' ? 'Spanish' : 'English'} words yet: this bulletin will leave it out.</span>}
        <div className="template-actions">
          {part && <button type="button" disabled={busy || !!editor} onClick={() => setEditor({
            id: part.id, revision: part.revision, name: part.name, after: part.after,
            words_en: part.words_en, words_es: part.words_es, in_library: part.in_library,
          })}>Edit words</button>}
          <button type="button" disabled={busy} onClick={() => change(uses.filter(u => u.id !== use.id))}>Remove from this service</button>
        </div>
      </div>;
    })}

    {!editor && <div className="template-actions">
      {library.length > 0 && <>
        <label>From the library <select value={pick} onChange={e => setPick(e.target.value)}>
          <option value="">Choose a part</option>
          {library.map(p => <option key={p.id} value={p.id}>{p.name} (after {placeLabel(p.after)})</option>)}
        </select></label>
        <button type="button" disabled={!pick || uses.length >= 8} onClick={() => {
          const part = partOf(Number(pick));
          if (part) change([...uses, { id: part.id, after: part.after }]);
          setPick('');
        }}>Add to this service</button>
      </>}
      <button type="button" disabled={uses.length >= 8 || places.length === 0} onClick={() => setEditor({
        name: '', after: 'peace', words_en: '', words_es: '', in_library: true,
      })}>Write a new part</button>
    </div>}

    {editor && <div className="template-editor added-part-editor">
      <p><strong>{editor.id ? 'Edit part' : 'New part'}</strong>{editor.id ? ': the words change for every Sunday that uses this part.' : ''}</p>
      <label>Name <input value={editor.name} maxLength={120} placeholder="First Communion"
        onChange={e => setEditor({ ...editor, name: e.target.value })} /></label>
      <label>Usually goes after <select value={editor.after} onChange={e => setEditor({ ...editor, after: e.target.value })}>
        {places.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
      </select></label>
      <p className="added-part-marks">One line is one paragraph. Start a line with <code>#</code> for a heading, <code>~</code> for a direction (italic), <code>*</code> for the people’s words (bold); a line with no mark is the celebrant’s. A blank line leaves a space before the next line.</p>
      <div className="added-part-words">
        <label>English <textarea rows={8} value={editor.words_en} placeholder={'# FIRST COMMUNION\n~ The children come forward.\nN., ...\n* Amen.'}
          onChange={e => setEditor({ ...editor, words_en: e.target.value })} /></label>
        <Preview words={editor.words_en} />
      </div>
      <div className="added-part-words">
        <label>Spanish <textarea rows={8} value={editor.words_es} placeholder={'# PRIMERA COMUNIÓN\n~ ...'}
          onChange={e => setEditor({ ...editor, words_es: e.target.value })} /></label>
        <Preview words={editor.words_es} />
      </div>
      <label className="added-part-library"><input type="checkbox" checked={editor.in_library}
        onChange={e => setEditor({ ...editor, in_library: e.target.checked })} /> Keep in the library for other Sundays</label>
      <div className="template-actions">
        <button type="button" disabled={busy || !editor.name.trim() || (!editor.words_en.trim() && !editor.words_es.trim())} onClick={save}>
          {busy ? 'Saving…' : editor.id ? 'Save part' : 'Save and add to this service'}</button>
        <button type="button" disabled={busy} onClick={() => setEditor(null)}>Cancel</button>
      </div>
    </div>}
  </div>;
};
